import React, { useState, useEffect } from "react";
import SubTabs from "../top_bar_components/SubTabs";
import ReactToPrint from "react-to-print";
import PrintIcon from "@mui/icons-material/Print";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BarChartIcon from "@mui/icons-material/BarChart";
import AssignmentIcon from "@mui/icons-material/Assignment";
import axios from "axios";
import { get_t_str_shortened_max } from "../../../../helpers/helpers";
import { get_t_str_minute_precision } from "../../../../helpers/helpers";
import { process_used_schedule_data } from "../../../../helpers/helpers";
import { formatDateReadableWithDayOfWeek } from "../../requests/utilities/helpers";
import { getDefaultAxiosConfig } from "../../../../utilities/requests";
import { API_send_notification_to_employee } from "../../../../constants";
import { getStoreNow } from "../../live_day_labor/LiveDayLaborCore";
import dayjs from "dayjs";
import {
  filterCoveredNoShowRows,
  getCoveredPublishedKey,
  getCoveredPublishedShifts,
  sumCoveredPublishedHours,
} from "./attendanceCoverage";
import { describeEmptyActualTable } from "./actualShiftTimes";
import "./VisualDailyReportModal2.css";


const SNAP_INTERVAL_MINUTES = 15;
const SNAP_INTERVAL_HOURS = SNAP_INTERVAL_MINUTES / 60;
const LABOR_LAW_BREAK_TYPES = ["meal_break", "meal_break_timing", "rest_break", "rest_break_timing"];
const hasLaborLawBreakViolation = (laborViolations, stmId) =>
  (laborViolations || []).some(
    (v) => v.store_team_member_id === stmId && LABOR_LAW_BREAK_TYPES.includes(v.regulation_type)
  );
const MISSED_SHIFT_REASON_OPTIONS = [
  { value: "called_off_sick", label: "Called out sick" },
  { value: "called_off_other", label: "Called out other" },
  { value: "no_show", label: "No-call no-show" },
  { value: "manager_cancelled", label: "Manager cancelled" },
];
const snapQhToMinuteIncrement = (qhValue, incrementMinutes = SNAP_INTERVAL_MINUTES) => {
  if (!Number.isFinite(Number(qhValue))) {
    return qhValue;
  }
  const totalMinutes = Math.floor(Number(qhValue) * 60 + 1e-6);
  const snappedMinutes = totalMinutes - (totalMinutes % incrementMinutes);
  return snappedMinutes / 60;
};

function loadPersistedSet(key) {
  try {
    const stored = localStorage.getItem(key);
    if (stored) return new Set(JSON.parse(stored));
  } catch {}
  return new Set();
}

function persistSet(key, set) {
  try {
    localStorage.setItem(key, JSON.stringify([...set]));
  } catch {}
}

export function LiveDayLaborReportContent(props) {
  const storageKey = `liveLaborAlerts-${props.store_id}-${props.date_str}`;
  const dismissedKey = `${storageKey}-dismissed`;
  const notifiedKey = `${storageKey}-notified`;

  const [dismissedAlerts, setDismissedAlerts] = useState(() => loadPersistedSet(dismissedKey));
  const [notifiedAlerts, setNotifiedAlerts] = useState(() => loadPersistedSet(notifiedKey));
  const [sendingAlerts, setSendingAlerts] = useState(new Set());
  const [classifyingAlerts, setClassifyingAlerts] = useState(new Set());
  const [classificationMenuAnchor, setClassificationMenuAnchor] = useState(null);
  const [classificationEvent, setClassificationEvent] = useState(null);

  // Reload from localStorage when store/date changes
  useEffect(() => {
    setDismissedAlerts(loadPersistedSet(dismissedKey));
    setNotifiedAlerts(loadPersistedSet(notifiedKey));
  }, [dismissedKey, notifiedKey]);

  useEffect(() => { persistSet(dismissedKey, dismissedAlerts); }, [dismissedKey, dismissedAlerts]);
  useEffect(() => { persistSet(notifiedKey, notifiedAlerts); }, [notifiedKey, notifiedAlerts]);

  const storeDayStartQhnum = Number.isFinite(Number(props.start_qhnum))
    ? Number(props.start_qhnum)
    : null;
  const storeDayEndQhnum =
    storeDayStartQhnum !== null ? storeDayStartQhnum + 24 : null;

  // Keep "live" visuals progressing even if backend qh snapshots lag briefly.
  const [clockTick, setClockTick] = React.useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setClockTick((c) => c + 1);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const currentBusinessQhNum = React.useMemo(() => {
    const storeNow = getStoreNow(props.storeTimezone);
    const now = dayjs(storeNow);
    const qhNow =
      now.hour() + now.minute() / 60 + now.second() / 3600;
    const businessQhNow =
      storeDayStartQhnum !== null && qhNow < storeDayStartQhnum
        ? qhNow + 24
        : qhNow;
    return snapQhToMinuteIncrement(businessQhNow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clockTick, storeDayStartQhnum, props.storeTimezone]);

  const isPastDate = (dateStr) => {
    if (!dateStr) {
      return false;
    }
    return dayjs(dateStr).isBefore(dayjs(getStoreNow(props.storeTimezone)), "day");
  };

  const getEffectiveShiftEnd = (shift, fallbackDateStr) => {
    const startValue = Number.isFinite(Number(shift?.shift_start))
      ? Number(shift.shift_start)
      : null;
    const rawEnd = shift?.shift_end;
    const hasRawEnd =
      rawEnd !== null &&
      rawEnd !== undefined &&
      rawEnd !== "" &&
      Number.isFinite(Number(rawEnd));
    let endValue = hasRawEnd ? Number(rawEnd) : null;
    const shiftDate = shift?.date_start
      ? String(shift.date_start).split("T")[0]
      : shift?.date
        ? String(shift.date).split("T")[0]
        : fallbackDateStr;
    if (!hasRawEnd && storeDayEndQhnum !== null && isPastDate(shiftDate)) {
      endValue = storeDayEndQhnum;
    }
    if (endValue !== null && startValue !== null && endValue < startValue) {
      endValue += 24;
    }
    return {
      endValue,
      hasEnd: endValue !== null,
      startValue,
      shiftDate,
    };
  };

  // Helper functions for breaks
  const getUnpaidBreaks = (shift) => {
    if (Array.isArray(shift?.unpaid_breaks) && shift.unpaid_breaks.length > 0) {
      return shift.unpaid_breaks;
    }

    if (shift?.break_length > 0 && typeof shift?.break_start === "number") {
      const unpaidBreaks = [
        {
          break_start: shift.break_start,
          break_length: shift.break_length,
        },
      ];
      if ((shift?.break_length_2 || 0) > 0 && typeof shift?.break_start_2 === "number") {
        unpaidBreaks.push({
          break_start: shift.break_start_2,
          break_length: shift.break_length_2,
        });
      }
      return unpaidBreaks;
    }

    return [];
  };

  const parseNumericValue = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  };

  const getUnpaidBreakDurationHours = (shift) => {
    if (Array.isArray(shift?.unpaid_breaks) && shift.unpaid_breaks.length > 0) {
      return shift.unpaid_breaks.reduce((sum, breakEntry) => {
        const breakLength = parseNumericValue(
          breakEntry?.break_length ?? breakEntry?.break_duration ?? 0
        );
        return sum + breakLength;
      }, 0);
    }

    return parseNumericValue(shift?.total_break || 0) || parseNumericValue(shift?.break_length) + parseNumericValue(shift?.break_length_2);
  };

  const getScheduledBreaks = (shift) => {
    const scheduledBreaks = [];
    if ((shift?.break_length || 0) > 0 && typeof shift?.break_start === "number") {
      scheduledBreaks.push({
        break_start: shift.break_start,
        break_length: shift.break_length,
      });
    }
    if ((shift?.break_length_2 || 0) > 0 && typeof shift?.break_start_2 === "number") {
      scheduledBreaks.push({
        break_start: shift.break_start_2,
        break_length: shift.break_length_2,
      });
    }
    return scheduledBreaks;
  };

  const getScheduledHours = (shift) => {
    if (!shift) {
      return 0;
    }
    return shift.hours ||
      (shift.shift_end - shift.shift_start - getUnpaidBreakDurationHours(shift));
  };

  const getCoveredPublishedHours = (shift) => {
    return sumCoveredPublishedHours(shift, getScheduledHours);
  };

  const formatCoveredPublishedTimeRange = (shift) => {
    const covered = getCoveredPublishedShifts(shift);
    if (covered.length === 0) {
      return "--";
    }
    if (covered.length === 1) {
      return `${get_t_str_minute_precision(covered[0].shift_start)} - ${get_t_str_minute_precision(covered[0].shift_end)}`;
    }
    return `${get_t_str_minute_precision(covered[0].shift_start)} - ${get_t_str_minute_precision(covered[covered.length - 1].shift_end)} (${covered.length} shifts)`;
  };

  // Process used schedule data
  const used_schedule_data = process_used_schedule_data(
    filterCoveredNoShowRows(props.actual_used_schedule)
  );

  // Check for non-coverage shifts
  let show_non_coverage_used = false;
  show_non_coverage_used = used_schedule_data?.some(
    (shift) => getCoveredPublishedShifts(shift).some(
      (publishedShift) => publishedShift?.role_id?.exclude_from_labor === true
    )
  );

  const getStartTime = (shift) => {
    if (
      shift.shift_start === 0 &&
      shift.shift_end === 0 &&
      shift.published &&
      shift.published.shift_start !== undefined
    ) {
      return shift.published.shift_start;
    }
    return shift.shift_start;
  };

  let transformed_used_schedule_data = used_schedule_data?.sort((a, b) => {
    const startTimeA = getStartTime(a);
    const startTimeB = getStartTime(b);
    return startTimeA - startTimeB;
  });

  const used_stm_members_dict = {};
  const used_stm_is_minor_dict = {};
  props.store_team_member_weekly_info?.forEach((member) => {
    if (member.user_id != null) {
      used_stm_members_dict[member.id] = member.hours;
    }
    used_stm_is_minor_dict[member.id] = member.is_minor;
  });

  const transformed_used_schedule_data2 = transformed_used_schedule_data?.map(
    (item) => {
      const total_weekly_hours =
        used_stm_members_dict[item.store_team_member.id];
      const is_minor = used_stm_is_minor_dict[item.store_team_member.id];
      return {
        ...item,
        total_weekly_hours:
          total_weekly_hours !== undefined ? total_weekly_hours : null,
        is_minor: is_minor !== undefined ? is_minor : null,
      };
    }
  );

  const groupedUsedData = transformed_used_schedule_data2?.reduce(
    (acc, item) => {
      const unpaidBreakDuration = getUnpaidBreakDurationHours(item);
      if (!acc[item.stm_id]) {
        acc[item.stm_id] = {
          stm_id: item.stm_id,
          name: item.name,
          total_weekly_hours: item.total_weekly_hours,
          is_minor: item.is_minor,
          shifts: [],
        };
      }

      const {
        endValue: shiftEndValue,
        startValue: shiftStartValue,
      } = getEffectiveShiftEnd(item, props.date_str);
      const shiftStart = shiftStartValue ?? item.shift_start;
      const isLiveShift = item?.result_end === "in_progress";
      const displayShiftEnd =
        isLiveShift && shiftStart != null
          ? Math.max(shiftEndValue ?? shiftStart, currentBusinessQhNum)
          : shiftEndValue;
      const hasDisplayShiftEnd =
        displayShiftEnd !== null && displayShiftEnd !== undefined && shiftStart != null;
      const shiftLength = hasDisplayShiftEnd ? displayShiftEnd - shiftStart : 0;
      const hoursWorked = hasDisplayShiftEnd
        ? Math.max(0, shiftLength - unpaidBreakDuration)
        : 0;
      const publishedHours = getCoveredPublishedHours(item);

      acc[item.stm_id].shifts.push({
        shift_start: shiftStart,
        shift_end: displayShiftEnd,
        shift_length: shiftLength,
        break_start: item.break_start,
        break_length: item.break_length || 0,
        break_start_2: item.break_start_2,
        break_length_2: item.break_length_2 || 0,
        total_break: unpaidBreakDuration,
        paid_breaks: item.paid_breaks || [],
        unpaid_breaks: item.unpaid_breaks || [],
        date: item.date_start,
        hours: hoursWorked,
        result_start: item.result_start,
        result_end: item.result_end,
        published: item.published,
        covered_published_schedules: item.covered_published_schedules || [],
        delta: hasDisplayShiftEnd ? hoursWorked - publishedHours : null,
        has_shift_end: hasDisplayShiftEnd,
      });

      return acc;
    },
    {}
  ) || {};

  const transformed_used_schedule_data3 = Object.values(groupedUsedData);

  transformed_used_schedule_data3.sort((a, b) => {
    const startTimeA = getStartTime(a.shifts[0]);
    const startTimeB = getStartTime(b.shifts[0]);
    return startTimeA - startTimeB;
  });

  // Calculate time axis - combine both actual used and actual schedule for proper range
  const actualScheduleSource = props.actual_schedule || [];
  const actualUsedScheduleSource = props.actual_used_schedule || [];
  const schedule_source = [...actualUsedScheduleSource, ...actualScheduleSource];

  const scheduleQhNums = schedule_source
    .filter((d) => !(d.shift_start === 0 && d.shift_end === 0))
    .flatMap((d) => {
      const { startValue, endValue } = getEffectiveShiftEnd(d, props.date_str);
      if (startValue === null && endValue === null) {
        return [];
      }
      const values = [];
      if (startValue !== null) {
        values.push(startValue);
      }
      if (endValue !== null) {
        values.push(endValue);
      }
      return values;
    })
    .filter((v) => !isNaN(v));

  let start_qhnum = 8; // Default start
  let end_qhnum = 22; // Default end

  if (scheduleQhNums.length > 0) {
    start_qhnum = Math.floor(Math.min(...scheduleQhNums));
    end_qhnum = Math.ceil(Math.max(...scheduleQhNums));
  }

  const forecastedLaborEndQhNum = Array.isArray(props.forecasted_labor_day)
    ? props.forecasted_labor_day
      .filter(
        (entry) =>
          Number(entry?.hours) > 0 &&
          Number.isFinite(Number(entry?.qh_num))
      )
      .reduce((maxQh, entry) => Math.max(maxQh, Number(entry.qh_num)), Number.NEGATIVE_INFINITY)
    : Number.NEGATIVE_INFINITY;

  if (Number.isFinite(forecastedLaborEndQhNum)) {
    end_qhnum = Math.max(end_qhnum, Math.ceil(forecastedLaborEndQhNum));
  }

  const numOfHourBoxes = end_qhnum - start_qhnum;
  const widthOfEachHourBox = numOfHourBoxes > 0 ? 100 / numOfHourBoxes : 0;
  const widthOfEachHalfTick = widthOfEachHourBox / 2;
  const widthOfEachQuarterTick = widthOfEachHourBox / 4;

  // Metrics data
  const metricsData = props.actual_intraday_metrics;
  const earnedSupportLaborKey = "Earned Support Labor";
  const derivedMetricsData = React.useMemo(() => {
    if (!metricsData) {
      return metricsData;
    }
    if (Array.isArray(metricsData[earnedSupportLaborKey])) {
      return metricsData;
    }
    const earnedHoursSeries = Array.isArray(metricsData["Earned Hours"])
      ? metricsData["Earned Hours"]
      : [];
    if (earnedHoursSeries.length === 0) {
      return metricsData;
    }
    return {
      ...metricsData,
      [earnedSupportLaborKey]: earnedHoursSeries.map((entry) => ({
        ...entry,
        total_value: (Number(entry?.total_value) || 0) * 2,
      })),
    };
  }, [metricsData]);

  // Forecast totals from daily_intraday_metrics
  const forecastMetricsData = props.daily_intraday_metrics;

  const totalForecastedSales = forecastMetricsData?.["Sales"]?.reduce(
    (acc, obj) => acc + obj.total_value,
    0
  );
  const totalForecastedTransactions = forecastMetricsData?.["Transactions"]?.reduce(
    (acc, obj) => acc + obj.total_value,
    0
  );
  const totalForecastedItems = forecastMetricsData?.["Items"]?.reduce(
    (acc, obj) => acc + obj.total_value,
    0
  );

  // Variance calculation helper
  const calculateVariancePercent = (actual, forecast) => {
    if (!forecast || forecast === 0) return 0;
    return ((actual - forecast) / forecast) * 100;
  };

  const primaryHoursKey = "Earned Hours";
  const secondaryHoursKey = "Actual Used Hours";
  const trainingHoursKey = "Training Hours";
  const adminHoursKey = "Admin Hours";
  const primaryLaborKey = "Earned Labor";
  const primaryLaborDataKey = Array.isArray(derivedMetricsData?.[earnedSupportLaborKey])
    ? earnedSupportLaborKey
    : primaryLaborKey;
  const secondaryLaborKey = "Actual Used Labor";

  const shouldUseDailyEarnedTotals = Array.isArray(props.calculated_earned_labor_day);
  const shouldUseDailyActualTotals = Array.isArray(props.actual_used_labor_vs_earned_day);
  const totalTrainingHours = derivedMetricsData?.[trainingHoursKey]?.reduce(
    (acc, obj) => acc + obj.total_value,
    0
  );
  const totalAdminHours = derivedMetricsData?.[adminHoursKey]?.reduce(
    (acc, obj) => acc + obj.total_value,
    0
  );

  // Build filtered metrics
  const filteredMetrics = {};
  const buildHalfHourSeries = (metric) => {
    const series = [];
    const metricArray = derivedMetricsData?.[metric] || [];
    for (let h = start_qhnum; h < end_qhnum; h += 0.5) {
      const entry = metricArray.find((i) => Math.abs(i.qh_num - h) < 0.001);
      series.push({ qh_num: h, total_value: entry ? entry.total_value : 0 });
    }
    return series;
  };

  [
    "Sales",
    "Transactions",
    "Items",
    primaryLaborDataKey,
    primaryHoursKey,
    secondaryLaborKey,
    secondaryHoursKey,
    trainingHoursKey,
    adminHoursKey,
  ].forEach((metric) => {
    if (derivedMetricsData?.[metric]) {
      filteredMetrics[metric] = buildHalfHourSeries(metric);
    }
  });

  // Build filtered forecast metrics
  const filteredForecastMetrics = {};
  const buildForecastHalfHourSeries = (metric) => {
    const series = [];
    const metricArray = forecastMetricsData?.[metric] || [];
    for (let h = start_qhnum; h < end_qhnum; h += 0.5) {
      const entry = metricArray.find((i) => Math.abs(i.qh_num - h) < 0.001);
      series.push({ qh_num: h, total_value: entry ? entry.total_value : 0 });
    }
    return series;
  };

  ["Sales", "Transactions", "Items"].forEach((metric) => {
    if (forecastMetricsData?.[metric]) {
      filteredForecastMetrics[metric] = buildForecastHalfHourSeries(metric);
    }
  });

  const lastUpdatedQhNumFromLabor = React.useMemo(() => {
    if (!Array.isArray(props.actual_used_labor_vs_earned_day) || props.actual_used_labor_vs_earned_day.length === 0) {
      return null;
    }
    const entriesWithHours = props.actual_used_labor_vs_earned_day.filter(item => item.hours > 0);
    if (entriesWithHours.length === 0) {
      return null;
    }
    // Labor qh_num represents the start of the bucket. "Last Updated" should
    // represent the end of that bucket (e.g. 18.5 -> 18.75 / 6:45pm).
    const bucketEndQhNum =
      Math.max(...entriesWithHours.map(item => item.qh_num)) + SNAP_INTERVAL_HOURS;
    return bucketEndQhNum;
  }, [props.actual_used_labor_vs_earned_day]);

  const lastUpdatedQhNumFromSchedule = React.useMemo(() => {
    if (
      !Array.isArray(transformed_used_schedule_data3) ||
      transformed_used_schedule_data3.length === 0
    ) {
      return null;
    }
    const shiftEnds = transformed_used_schedule_data3
      .flatMap((employee) => employee?.shifts || [])
      .map((shift) => Number(shift?.shift_end))
      .filter(
        (value) =>
          Number.isFinite(value) &&
          value > 0 &&
          value !== Number.NEGATIVE_INFINITY &&
          value !== Number.POSITIVE_INFINITY
      );
    if (shiftEnds.length === 0) {
      return null;
    }
    return Math.max(...shiftEnds);
  }, [transformed_used_schedule_data3]);

  const lastUpdatedQhNumFromEarned = React.useMemo(() => {
    if (!Array.isArray(props.calculated_earned_labor_day) || props.calculated_earned_labor_day.length === 0) {
      return null;
    }
    const earnedEntriesWithHours = props.calculated_earned_labor_day.filter(
      (item) => Number(item?.hours) > 0 && Number.isFinite(Number(item?.qh_num))
    );
    if (earnedEntriesWithHours.length === 0) {
      return null;
    }
    // Earned labor qh_num is the bucket start; display should indicate
    // the end of the most recently completed bucket.
    return (
      Math.max(...earnedEntriesWithHours.map((item) => Number(item.qh_num))) +
      SNAP_INTERVAL_HOURS
    );
  }, [props.calculated_earned_labor_day]);

  const maxCalculatedEarnedQhNum = React.useMemo(() => {
    if (!Array.isArray(props.calculated_earned_labor_day) || props.calculated_earned_labor_day.length === 0) {
      return null;
    }
    const qhNums = props.calculated_earned_labor_day
      .map((entry) => Number(entry.qh_num))
      .filter(
        (value) =>
          Number.isFinite(value) &&
          value >= 0 &&
          value !== Number.NEGATIVE_INFINITY &&
          value !== Number.POSITIVE_INFINITY
      );
    if (qhNums.length === 0) {
      return null;
    }
    return Math.max(...qhNums);
  }, [props.calculated_earned_labor_day]);

  const hasInProgressShift = React.useMemo(() => {
    if (
      !Array.isArray(transformed_used_schedule_data3) ||
      transformed_used_schedule_data3.length === 0
    ) {
      return false;
    }
    return transformed_used_schedule_data3.some((employee) =>
      (employee?.shifts || []).some(
        (shift) => shift?.result_end === "in_progress"
      )
    );
  }, [transformed_used_schedule_data3]);

  const rawLastUpdatedQhNum =
    lastUpdatedQhNumFromEarned ??
    lastUpdatedQhNumFromLabor ??
    lastUpdatedQhNumFromSchedule;
  const fallbackQhNum =
    maxCalculatedEarnedQhNum ?? lastUpdatedQhNumFromLabor ?? start_qhnum;
  const baselineLastUpdatedQhNum = rawLastUpdatedQhNum ?? fallbackQhNum;

  // Monotonic in-session progression for active shifts so the line keeps
  // advancing even if API snapshots lag or local/store timezone differs.
  const progressAnchorRef = React.useRef({
    qh: null,
    ms: null,
    dateStr: null,
  });

  const progressedQhNum = React.useMemo(() => {
    if (!Number.isFinite(Number(baselineLastUpdatedQhNum))) {
      return baselineLastUpdatedQhNum;
    }

    const anchor = progressAnchorRef.current;
    const nowMs = Date.now();
    const baseline = Number(baselineLastUpdatedQhNum);
    const nowBusinessQhNum = Number(currentBusinessQhNum);

    const shouldResetAnchor =
      anchor.qh === null ||
      anchor.ms === null ||
      anchor.dateStr !== props.date_str ||
      !hasInProgressShift ||
      baseline >
        Number(anchor.qh) +
          Math.max(0, nowMs - Number(anchor.ms)) / (1000 * 60 * 60);

    if (shouldResetAnchor) {
      // Backdate the anchor when baseline is stale so progression reflects
      // the current quarter immediately instead of waiting another 15 minutes.
      const lagHours =
        Number.isFinite(nowBusinessQhNum) && nowBusinessQhNum > baseline
          ? nowBusinessQhNum - baseline
          : 0;
      progressAnchorRef.current = {
        qh: baseline,
        ms: nowMs - lagHours * 60 * 60 * 1000,
        dateStr: props.date_str,
      };
      return baseline + lagHours;
    }

    const elapsedHours =
      Math.max(0, nowMs - Number(anchor.ms)) / (1000 * 60 * 60);
    return Number(anchor.qh) + elapsedHours;
  }, [
    baselineLastUpdatedQhNum,
    clockTick,
    currentBusinessQhNum,
    props.date_str,
    hasInProgressShift,
  ]);

  let computedLastUpdatedQhNum = baselineLastUpdatedQhNum;
  if (hasInProgressShift) {
    computedLastUpdatedQhNum = Math.max(
      computedLastUpdatedQhNum,
      currentBusinessQhNum,
      progressedQhNum
    );
  }
  // Clamp to actual current time so "Last Updated" never shows a future time
  // (e.g. when the last bucket is still in progress but its end-of-bucket
  // timestamp is ahead of now).
  if (Number.isFinite(computedLastUpdatedQhNum) && Number.isFinite(currentBusinessQhNum)) {
    computedLastUpdatedQhNum = Math.min(computedLastUpdatedQhNum, currentBusinessQhNum);
  }

  const lastUpdatedQhNum =
    computedLastUpdatedQhNum === null
      ? null
      : snapQhToMinuteIncrement(computedLastUpdatedQhNum);

  // Convert lastUpdatedQhNum to a display time string
  const lastUpdatedTimeString = React.useMemo(() => {
    if (lastUpdatedQhNum === null) return null;
    // Handle overnight schedules (qh > 24)
    let displayQh = lastUpdatedQhNum;
    if (displayQh >= 24) {
      displayQh = displayQh - 24;
    }
    const totalMinutes = Math.floor(displayQh * 60 + 1e-6);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  }, [lastUpdatedQhNum]);

  // Use lastUpdatedQhNum for line position and calculations
  const dataQhNum = lastUpdatedQhNum;
  const isDataLineVisible = dataQhNum >= start_qhnum && dataQhNum <= end_qhnum;

  const sumHalfHourMetricUpToNow = React.useCallback(
    (metricKey) => {
      const metricValues = derivedMetricsData?.[metricKey] || [];
      const cutoffQhNum = Number(dataQhNum);
      if (!Number.isFinite(cutoffQhNum)) {
        return metricValues.reduce((acc, obj) => acc + (obj?.total_value || 0), 0);
      }
      return metricValues.reduce(
        (acc, obj) => (obj.qh_num + 0.5 <= cutoffQhNum ? acc + (obj?.total_value || 0) : acc),
        0
      );
    },
    [derivedMetricsData, dataQhNum]
  );

  const sumQuarterHourHoursUpToNow = React.useCallback((entries) => {
    if (!Array.isArray(entries)) {
      return null;
    }
    const cutoffQhNum = Number(dataQhNum);
    if (!Number.isFinite(cutoffQhNum)) {
      return entries.reduce((acc, entry) => acc + (Number(entry?.hours) || 0), 0);
    }
    return entries.reduce((acc, entry) => {
      const qhNum = Number(entry?.qh_num);
      const hours = Number(entry?.hours);
      if (!Number.isFinite(qhNum) || !Number.isFinite(hours)) {
        return acc;
      }
      return qhNum + SNAP_INTERVAL_HOURS <= cutoffQhNum ? acc + hours : acc;
    }, 0);
  }, [dataQhNum]);

  const totalSales = sumHalfHourMetricUpToNow("Sales");
  const totalTransactions = sumHalfHourMetricUpToNow("Transactions");
  const totalItems = sumHalfHourMetricUpToNow("Items");
  const totalPrimaryHours = shouldUseDailyEarnedTotals
    ? sumQuarterHourHoursUpToNow(props.calculated_earned_labor_day) ??
      sumHalfHourMetricUpToNow(primaryHoursKey)
    : sumHalfHourMetricUpToNow(primaryHoursKey);
  const totalSecondaryHours = shouldUseDailyActualTotals
    ? sumQuarterHourHoursUpToNow(props.actual_used_labor_vs_earned_day) ??
      sumHalfHourMetricUpToNow(secondaryHoursKey)
    : sumHalfHourMetricUpToNow(secondaryHoursKey);

  // Calculate forecasted totals up to the last updated time (for meaningful variance comparison)
  const totalForecastedSalesUpToNow = forecastMetricsData?.["Sales"]?.reduce(
    (acc, obj) => obj.qh_num + 0.5 <= dataQhNum ? acc + obj.total_value : acc,
    0
  );
  const totalForecastedTransactionsUpToNow = forecastMetricsData?.["Transactions"]?.reduce(
    (acc, obj) => obj.qh_num + 0.5 <= dataQhNum ? acc + obj.total_value : acc,
    0
  );
  const totalForecastedItemsUpToNow = forecastMetricsData?.["Items"]?.reduce(
    (acc, obj) => obj.qh_num + 0.5 <= dataQhNum ? acc + obj.total_value : acc,
    0
  );

  // Calculate the left position as a percentage within the 62% chart area
  const dataLinePositionInChart = (dataQhNum - start_qhnum) * widthOfEachHourBox;
  const clampedDataLinePositionInChart = Math.max(
    0,
    Math.min(100, dataLinePositionInChart)
  );
  const dataLineLabelTransform =
    clampedDataLinePositionInChart >= 98
      ? "translateX(-100%)"
      : clampedDataLinePositionInChart <= 2
        ? "translateX(0)"
        : "translateX(-50%)";

  // Live Activity Feed — actionable alerts for active shifts
  const activityEvents = React.useMemo(() => {
    if (!props.showActivityFeed) return [];
    const events = [];
    const now = lastUpdatedQhNum;
    if (now === null) return [];

    // Per-store live-alert thresholds. Each accessor falls back to the
    // hard-coded literal when the config (or a field) is absent, so a
    // default/absent config reproduces prior behavior exactly. `cfgOn`
    // defaults to enabled so an absent config keeps every alert firing.
    const cfg = props.liveAlertConfig || {};
    const cfgNum = (field, fallback) => {
      const v = cfg[field];
      return v === null || v === undefined || v === "" ? fallback : Number(v);
    };
    const cfgOn = (field) => cfg[field] !== false;
    const formatElapsedFromMinutes = (minutesElapsed) => {
      if (minutesElapsed >= 120) {
        const hoursElapsed = Math.round((minutesElapsed / 60) * 10) / 10;
        return `${hoursElapsed} hours`;
      }
      return `${minutesElapsed} min`;
    };

    transformed_used_schedule_data3?.forEach((employee) => {
      // Calculate today's hours for this employee across all shifts
      const todayHours = employee.shifts.reduce((sum, s) => {
        if (!(s.shift_start === 0 && s.shift_end === 0)) {
          const end = s.result_end === "in_progress" ? now : s.shift_end;
          if (end > s.shift_start) {
            const unpaidBreak = getUnpaidBreakDurationHours(s);
            return sum + (end - s.shift_start) - unpaidBreak;
          }
        }
        return sum;
      }, 0);
      const totalWeeklyPlusTodayHours = (employee.total_weekly_hours || 0) + todayHours;

      // 6. Approaching overtime (fallback when labor law violations are not available)
      if (cfgOn("alert_approaching_weekly_ot_enabled") && (!props.labor_violations || props.labor_violations.length === 0) && employee.total_weekly_hours != null && totalWeeklyPlusTodayHours >= cfgNum("weekly_ot_warning_hours", 35)) {
        events.push({
          key: `${employee.stm_id}-overtime-${totalWeeklyPlusTodayHours >= 39 ? "alert" : "warning"}`,
          severity: totalWeeklyPlusTodayHours >= 39 ? "alert" : "warning",
          name: employee.name,
          message: totalWeeklyPlusTodayHours >= 40
            ? `Has worked ${Math.round(totalWeeklyPlusTodayHours * 10) / 10}hr this week — in overtime`
            : `Has worked ${Math.round(totalWeeklyPlusTodayHours * 10) / 10}hr this week — approaching 40hr overtime threshold`,
        });
      }

      // 7. Minor labor compliance (fallback when labor law violations are not available)
      if ((!props.labor_violations || props.labor_violations.length === 0) && employee.is_minor === true) {
        // alert_minor_daily_hours_enabled only governs the daily-hours alert.
        // The weekly minor warning has no separate control, so it stays on.
        if (cfgOn("alert_minor_daily_hours_enabled") && todayHours >= cfgNum("minor_daily_hours", 6)) {
          events.push({
            key: `${employee.stm_id}-minor-daily`,
            severity: todayHours >= 7.5 ? "alert" : "warning",
            name: employee.name,
            message: `Is a minor and has worked ${Math.round(todayHours * 10) / 10}hr today`,
          });
        }
        if (employee.total_weekly_hours != null && totalWeeklyPlusTodayHours >= 35) {
          events.push({
            key: `${employee.stm_id}-minor-weekly`,
            severity: totalWeeklyPlusTodayHours >= 39 ? "alert" : "warning",
            name: employee.name,
            message: `Is a minor and has worked ${Math.round(totalWeeklyPlusTodayHours * 10) / 10}hr this week`,
          });
        }
      }

      employee.shifts.forEach((shift) => {
        const isActive = shift.result_end === "in_progress";
        const isNoShow = shift.result_end === "no show" ||
          (shift.shift_start === 0 && shift.shift_end === 0 && shift.published);
        const pubStart = shift.published?.shift_start;
        const publishedScheduleId = shift.published?.id;
        const missedShiftReason = shift.published?.missed_shift_reason;
        const rawPubEnd = shift.published?.shift_end;
        const pubEnd = rawPubEnd != null && pubStart != null && rawPubEnd < pubStart
          ? rawPubEnd + 24
          : rawPubEnd;
        const normBreakQh = (qh) =>
          qh != null && pubStart != null && qh < pubStart ? qh + 24 : qh;
        const scheduledBreaks = getScheduledBreaks(shift.published).map((b) => ({
          ...b,
          break_start: normBreakQh(b.break_start),
        }));
        const relatedShiftEntries = employee.shifts.filter(
          (s) =>
            s.published?.shift_start === pubStart &&
            s.published?.shift_end === rawPubEnd
        );
        const relatedUnpaidBreaks = relatedShiftEntries.flatMap(
          (s) => s.unpaid_breaks || []
        );
        const activeBreak = relatedUnpaidBreaks.find((b) => b.is_in_progress);
        const completedUnpaidBreakCount = relatedUnpaidBreaks.filter(
          (breakEntry) => !breakEntry.is_in_progress
        ).length;
        const activeScheduledBreak =
          activeBreak && scheduledBreaks.length > 0
            ? scheduledBreaks[
                Math.min(completedUnpaidBreakCount, scheduledBreaks.length - 1)
              ]
            : null;
        const nextScheduledBreak =
          !activeBreak && scheduledBreaks.length > completedUnpaidBreakCount
            ? scheduledBreaks[completedUnpaidBreakCount]
            : null;

        // Skip dummy entries
        if (shift.shift_start === 0 && shift.shift_end === 0 && !shift.published) return;

        // 8. Unscheduled clock-in (no published shift)
        if (cfgOn("alert_unscheduled_clock_in_enabled") && shift.published === null && !(shift.shift_start === 0 && shift.shift_end === 0)) {
          events.push({
            key: `${employee.stm_id}-unscheduled-${shift.shift_start}`,
            severity: "warning",
            name: employee.name,
            message: `Clocked in with no published shift scheduled`,
          });
        }

        // 9. Early clock-in (clocked in 15+ min before scheduled shift)
        if (shift.published && pubStart != null && !(shift.shift_start === 0 && shift.shift_end === 0) && shift.shift_start < pubStart) {
          const minsEarly = Math.round((pubStart - shift.shift_start) * 60);
          if (cfgOn("alert_early_clock_in_enabled") && minsEarly >= cfgNum("early_clock_in_minutes", 15)) {
            const earlyDuration = formatElapsedFromMinutes(minsEarly);
            events.push({
              key: `${employee.stm_id}-early-${shift.shift_start}`,
              severity: minsEarly >= 60 ? "alert" : "warning",
              name: employee.name,
              message: `Clocked in ${earlyDuration} before scheduled shift`,
            });
          }
        }

        // 1. Shift was scheduled to start X min ago — hasn't clocked in
        if (isNoShow && shift.published && missedShiftReason) {
          return;
        }

        if (isNoShow && shift.published && pubStart != null && now > pubStart + cfgNum("no_show_minutes", 0) / 60) {
          if (cfgOn("alert_no_show_enabled")) {
            const minsLate = Math.round((now - pubStart) * 60);
            const lateDuration = formatElapsedFromMinutes(minsLate);
            events.push({
              key: `${employee.stm_id}-noshow-${publishedScheduleId || pubStart}`,
              stm_id: employee.stm_id,
              published_schedule_id: publishedScheduleId,
              severity: minsLate >= 30 ? "alert" : "warning",
              name: employee.name,
              message: `Shift was scheduled to start ${lateDuration} ago — hasn't clocked in`,
              notifyMessage: `Your shift started ${lateDuration} ago`,
              attendanceClassifiable: Boolean(props.post_missed_shift && publishedScheduleId),
            });
          }
          return;
        }

        // Remaining events only for active shifts
        if (!isActive) return;

        // 2. Shift was scheduled to end X min ago — hasn't clocked out.
        // late_clock_out_minutes is a grace period: don't alert until the
        // employee is that many minutes past their scheduled end.
        if (cfgOn("alert_late_clock_out_enabled") && pubEnd != null && now > pubEnd + cfgNum("late_clock_out_minutes", 30) / 60) {
          const minsOver = Math.round((now - pubEnd) * 60);
          const overDuration = formatElapsedFromMinutes(minsOver);
          events.push({
            key: `${employee.stm_id}-late-clockout-${pubEnd}`,
            stm_id: employee.stm_id,
            severity: "alert",
            name: employee.name,
            message: `Shift was scheduled to end ${overDuration} ago — hasn't clocked out`,
            notifyMessage: `Your shift ended ${overDuration} ago`,
          });
        }

        // 3. Break should have ended X min ago — hasn't resumed shift
        if (activeBreak && activeScheduledBreak?.break_length > 0) {
          const expectedBreakEnd =
            normBreakQh(activeBreak.break_start) + activeScheduledBreak.break_length;
          // break_overrun_minutes is a grace period: don't alert until the
          // break has run that many minutes past its scheduled end.
          if (cfgOn("alert_break_overrun_enabled") && now > expectedBreakEnd + cfgNum("break_overrun_minutes", 15) / 60) {
            const minsOver = Math.round((now - expectedBreakEnd) * 60);
            const breakOverDuration = formatElapsedFromMinutes(minsOver);
            events.push({
              key: `${employee.stm_id}-break-overrun-${shift.shift_start}`,
              stm_id: employee.stm_id,
              severity: "alert",
              name: employee.name,
              message: `Break should have ended ${breakOverDuration} ago — hasn't resumed shift`,
              notifyMessage: `Your break was scheduled to end ${breakOverDuration} ago`,
            });
          }
        }

        // Break-related events (only if break is scheduled and not yet taken)
        if (nextScheduledBreak && !activeBreak) {
          const diffMins = Math.round((now - nextScheduledBreak.break_start) * 60);

          // 4a. Scheduled break starting soon or just started (config min before to 5 min after)
          if (cfgOn("alert_break_reminder_enabled") && diffMins >= -cfgNum("break_reminder_minutes", 15) && diffMins <= 5) {
            const minsUntil = Math.abs(diffMins);
            events.push({
              key: `${employee.stm_id}-break-reminder-${shift.shift_start}`,
              stm_id: employee.stm_id,
              severity: "info",
              name: employee.name,
              message: diffMins <= 0
                ? (minsUntil === 0 ? `Scheduled break starting now` : `Scheduled break starting in ${minsUntil} min`)
                : `Scheduled break started ${minsUntil} min ago`,
              notifyMessage: diffMins <= 0
                ? (minsUntil === 0 ? `Your break is starting now` : `Your break is starting in ${minsUntil} min`)
                : `Your break was scheduled to start ${minsUntil} min ago`,
            });
          }
          // 4b. Scheduled break started X min ago — hasn't started break (after 5 min grace period).
          // Same scheduled-break alert path as 4a, so it shares the break-reminder toggle.
          else if (cfgOn("alert_break_reminder_enabled") && diffMins > 5) {
            const missedBreakDuration = formatElapsedFromMinutes(diffMins);
            events.push({
              key: `${employee.stm_id}-missed-break-${shift.shift_start}`,
              stm_id: employee.stm_id,
              severity: "warning",
              name: employee.name,
              message: `Scheduled break started ${missedBreakDuration} ago — hasn't started break`,
              notifyMessage: `Your break was scheduled to start ${missedBreakDuration} ago`,
            });
          }
        }

        // 5. Has worked X hours without taking a break. Falls back only when
        // labor law has no break violation for this employee, so another
        // employee's unrelated violation can't hide this alert.
        if (
          cfgOn("alert_no_break_long_shift_enabled") &&
          !hasLaborLawBreakViolation(props.labor_violations, employee.stm_id) &&
          nextScheduledBreak &&
          !activeBreak &&
          !(shift.shift_start === 0 && shift.shift_end === 0)
        ) {
          const hoursWorked = now - shift.shift_start;
          const requiredHoursBeforeBreak = (completedUnpaidBreakCount + 1) * cfgNum("no_break_shift_hours", 5);
          if (hoursWorked >= requiredHoursBeforeBreak) {
            events.push({
              key: `${employee.stm_id}-long-nobreak-${shift.shift_start}`,
              stm_id: employee.stm_id,
              severity:
                hoursWorked >= requiredHoursBeforeBreak + 1 ? "alert" : "warning",
              name: employee.name,
              message: `Has worked ${Math.floor(hoursWorked)} hours without taking a break`,
              notifyMessage: `You've been working for ${Math.floor(hoursWorked)} hours without a break`,
            });
          }
        }
      });
    });

    // Labor law violations from backend
    if (props.labor_violations) {
      const employeeLookup = {};
      transformed_used_schedule_data3?.forEach((emp) => {
        employeeLookup[emp.stm_id] = emp.name;
      });

      const abbreviateName = (fullName) => {
        if (!fullName) return "Unknown";
        const parts = fullName.trim().split(/\s+/);
        if (parts.length < 2) return fullName;
        return `${parts[0]} ${parts[parts.length - 1][0]}.`;
      };

      props.labor_violations.forEach((v) => {
        const fullName = v.store_team_member_name || employeeLookup[v.store_team_member_id] || "Unknown";
        events.push({
          key: `labor-law-${v.regulation_type}-${v.store_team_member_id}-${v.title || ""}-${v.details?.date || v.details?.week_number || v.shifts?.[0]?.id || ""}`,
          severity: v.severity === "ERROR" ? "alert" : "warning",
          name: abbreviateName(fullName),
          message: v.explanation || v.description || v.title,
          isLaborLaw: true,
        });
      });
    }

    // Sales vs forecast check (from 1pm onward)
    if (now >= 13 && totalForecastedSalesUpToNow && totalForecastedSalesUpToNow > 0) {
      const variancePct = calculateVariancePercent(totalSales, totalForecastedSalesUpToNow);
      if (cfgOn("alert_sales_off_forecast_enabled") && variancePct < -cfgNum("sales_off_forecast_pct", 10)) {
        events.push({
          key: "sales-below-forecast",
          severity: variancePct < -20 ? "alert" : "warning",
          name: "Sales",
          message: `Actual sales are likely to miss forecasted sales (down ${Math.abs(Math.round(variancePct))}%)`,
          isSalesAlert: true,
        });
      } else if (cfgOn("alert_sales_off_forecast_enabled") && variancePct > cfgNum("sales_off_forecast_pct", 10)) {
        events.push({
          key: "sales-above-forecast",
          severity: "info",
          name: "Sales",
          message: `Actual sales are likely to exceed forecasted sales (up ${Math.round(variancePct)}%)`,
          isSalesAlert: true,
        });
      }
    }

    // Actual labor vs earned labor check (from 1pm onward)
    if (now >= 13 && totalPrimaryHours && totalPrimaryHours > 0) {
      const laborVariancePct = calculateVariancePercent(totalSecondaryHours, totalPrimaryHours);
      if (cfgOn("alert_labor_over_forecast_enabled") && laborVariancePct > cfgNum("labor_over_forecast_pct", 10)) {
        events.push({
          key: "labor-above-earned",
          severity: laborVariancePct > 20 ? "alert" : "warning",
          name: "Labor",
          message: `Actual labor currently exceeds earned labor (up ${Math.round(laborVariancePct)}%)`,
          isLaborAlert: true,
        });
        // labor-below-earned is under-earned info with no manager control. The
        // over-forecast toggle/threshold is directional (+%), so it must NOT
        // gate this branch — otherwise disabling/raising over-forecast alerts
        // would also suppress this pre-existing under-labor signal.
      } else if (laborVariancePct < -10) {
        events.push({
          key: "labor-below-earned",
          severity: "info",
          name: "Labor",
          message: `Actual labor currently is less than earned labor (down ${Math.abs(Math.round(laborVariancePct))}%)`,
          isLaborAlert: true,
        });
      }
    }

    // Sort: labor law violations first, then by severity (alerts, warnings, info)
    const severityOrder = { alert: 0, warning: 1, info: 2 };
    events.sort((a, b) => {
      const aLaborLaw = a.isLaborLaw ? 0 : 1;
      const bLaborLaw = b.isLaborLaw ? 0 : 1;
      if (aLaborLaw !== bLaborLaw) return aLaborLaw - bLaborLaw;
      return severityOrder[a.severity] - severityOrder[b.severity];
    });
    return events;
  }, [transformed_used_schedule_data3, lastUpdatedQhNum, props.showActivityFeed, props.labor_violations, props.liveAlertConfig, props.post_missed_shift, totalSales, totalForecastedSalesUpToNow, totalPrimaryHours, totalSecondaryHours]);

  const closeClassificationMenu = () => {
    setClassificationMenuAnchor(null);
    setClassificationEvent(null);
  };

  const handleClassifyAttendance = async (reason) => {
    const event = classificationEvent;
    closeClassificationMenu();
    if (!event || !props.post_missed_shift) {
      return;
    }

    setClassifyingAlerts((prev) => new Set([...prev, event.key]));
    try {
      const result = await props.post_missed_shift(null, reason, {
        published_schedule_id: event.published_schedule_id,
      });
      if (result) {
        setDismissedAlerts((prev) => new Set([...prev, event.key]));
      }
    } catch (error) {
      console.error("Failed to classify missed shift:", error);
    } finally {
      setClassifyingAlerts((prev) => {
        const next = new Set(prev);
        next.delete(event.key);
        return next;
      });
    }
  };

  function formatName(name) {
    const words = name?.toLowerCase().split(" ");
    const formattedName = words?.map(
      (word) => word.charAt(0).toUpperCase() + word.slice(1)
    );
    return formattedName?.join(" ");
  }

  function convertToK(num) {
    let result = num / 1000;
    result = Math.round(result * 10) / 10;
    return result + "k";
  }

  let componentRef = React.useRef();

  const emptyActualTable = describeEmptyActualTable(props.actual_schedule);

  const renderUsedScheduleTable = () => {
    return (
      <>
        <div className="border border-slate-900">
          <div className="w-full border-b border-slate-900 flex">
            <div className="w-[38%] flex py-2 border-r border-slate-900">
              <div className="w-[22%] flex items-center px-4">
                <p className="text-slate-900 text-xs font-bold">Name</p>
              </div>
              <div className="w-[28%] text-center flex items-center justify-center">
                <p className="text-slate-900 text-xs font-bold">Actual Shift</p>
              </div>
              <div className="w-[28%] text-center flex items-center justify-center">
                <p className="text-slate-900 text-xs font-bold w-[85%]">
                  Published Shift
                </p>
              </div>
              <div className="w-[22%] text-center flex items-center justify-center">
                <p className="text-slate-900 text-xs font-bold w-[85%]">
                  Delta
                </p>
              </div>
            </div>
            <div className="w-[62%] flex">
              {Array.from({ length: numOfHourBoxes }).map((_, index) => (
                <div
                  key={index}
                  className={`${
                    index < numOfHourBoxes - 1 ? "border-r" : ""
                  } border-slate-900 flex flex-col`}
                  style={{ width: `${widthOfEachHourBox}%` }}
                >
                  <div className="h-[70%] flex items-center justify-center">
                    <p className="text-slate-900 text-xs">
                      {get_t_str_shortened_max(index + start_qhnum)}
                    </p>
                  </div>
                  <div className="h-[30%] flex">
                    <div className="w-[25%] border-r border-slate-900"></div>
                    <div className="w-[25%] border-r border-slate-900"></div>
                    <div className="w-[25%] border-r border-slate-900"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {transformed_used_schedule_data3.length === 0 && (
            // Sits above the Last Updated line so the line does not cut
            // through copy that says there is nothing to mark yet.
            <div className="w-full px-4 py-10 text-center relative z-50 bg-white">
              <p className="text-sm font-semibold text-slate-700">
                {emptyActualTable.title}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {emptyActualTable.description}
              </p>
            </div>
          )}
          {transformed_used_schedule_data3?.map((employee, index) => {
            // Track which published shifts have been rendered to avoid duplicates
            // (e.g. when an employee clocks out and back in for the same published shift)
            const seenPublishedKeys = new Set();
            // Aggregate actual hours per published shift so the first row
            // shows a combined delta and subsequent rows hide it.
            const publishedKeyActualHours = {};
            employee?.shifts?.forEach((shift) => {
              const key = getCoveredPublishedKey(shift);
              if (key) {
                publishedKeyActualHours[key] = (publishedKeyActualHours[key] || 0) + (shift.hours || 0);
              }
            });
            const showPublishedForShift = employee?.shifts?.map((shift) => {
              const key = getCoveredPublishedKey(shift);
              if (key) {
                if (!seenPublishedKeys.has(key)) {
                  seenPublishedKeys.add(key);
                  return true;
                }
              }
              return false;
            }) || [];
            const shiftCombinedDelta = employee?.shifts?.map((shift, idx) => {
              if (!showPublishedForShift[idx]) return null;
              const key = getCoveredPublishedKey(shift);
              const totalActual = publishedKeyActualHours[key] || 0;
              const publishedHours = getCoveredPublishedHours(shift);
              return totalActual - publishedHours;
            }) || [];

            return (
            <div
              key={index}
              className={`w-full ${
                index !== transformed_used_schedule_data3.length - 1
                  ? "border-b border-slate-900"
                  : ""
              } ${index % 2 === 0 ? "bg-white" : "bg-slate-100"}`}
            >
              {employee?.shifts?.map((shift, index2) => {
                const shiftStartValue = Number(shift.shift_start);
                const shiftEndValue = Number(shift.shift_end);
                const isShiftStartFinite = Number.isFinite(shiftStartValue);
                const isShiftEndFinite = Number.isFinite(shiftEndValue);
                const isLiveShift = shift.result_end === "in_progress";
                const renderedShiftEnd =
                  isLiveShift && Number.isFinite(Number(dataQhNum))
                    ? Number(dataQhNum)
                    : isShiftEndFinite
                      ? shiftEndValue
                      : null;
                const renderedShiftLength =
                  isShiftStartFinite && renderedShiftEnd !== null
                    ? Math.max(0, renderedShiftEnd - shiftStartValue)
                    : Math.max(0, Number(shift.shift_length) || 0);

                return (
                <div key={`${index2} - 2`} className="w-full flex">
                  <div
                    className={`w-[38%] flex py-3 border-r border-slate-900 relative z-30`}
                  >
                    <div className="w-[22%] flex items-center px-4">
                      {index2 === 0 && (
                        <div className="flex items-center gap-x-1">
                          <p className="text-slate-900 text-xs">
                            {formatName(employee.name)}
                          </p>
                          {employee.is_minor && (
                            <p className="text-xs font-bold">[m]</p>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="w-[28%] text-center flex items-center justify-center">
                      <p className="text-slate-900 text-xs">
                        {(shift.shift_start !== 0 || shift.shift_end !== 0) && (
                          <>
                            {get_t_str_minute_precision(shift.shift_start)}
                            {" - "}
                            {shift.result_end === 'in_progress' ? (
                              <span className="text-emerald-600 font-semibold">Active</span>
                            ) : (
                              shift.has_shift_end
                                ? get_t_str_minute_precision(shift.shift_end)
                                : "--"
                            )}
                          </>
                        )}
                      </p>
                    </div>
                    <div className="w-[28%] text-center flex items-center justify-center">
                      <p className="text-slate-900 text-xs">
                        {showPublishedForShift[index2] &&
                          formatCoveredPublishedTimeRange(shift)}
                      </p>
                    </div>
                    <div className="w-[22%] text-center flex items-center justify-center">
                      {showPublishedForShift[index2] ? (
                        <p
                          className={`${
                            shiftCombinedDelta[index2] > 0 ? "text-rose-700" : "text-emerald-700"
                          } text-xs`}
                        >
                          {shift.has_shift_end
                            ? `${shiftCombinedDelta[index2] > 0 ? "+" : ""}${shiftCombinedDelta[index2].toFixed(
                                2
                              )}hrs`
                            : "--"}
                        </p>
                      ) : getCoveredPublishedKey(shift) ? null : (
                        <p
                          className={`${
                            shift.delta > 0 ? "text-rose-700" : "text-emerald-700"
                          } text-xs`}
                        >
                          {shift.has_shift_end
                            ? `${shift.delta > 0 ? "+" : ""}${shift.delta.toFixed(
                                2
                              )}hrs`
                            : "--"}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="w-[62%] flex items-center relative">
                    {/* Published Shift Fill (white background) */}
                    {showPublishedForShift[index2] && getCoveredPublishedShifts(shift).map((publishedShift) => (
                      <div
                        key={`published-fill-${publishedShift.id || publishedShift.shift_start}`}
                        className={`h-7 absolute z-0 ${
                          publishedShift?.role_id?.exclude_from_labor === true
                            ? "bg-diagonal-stripes"
                            : "bg-white"
                        }`}
                        style={{
                          marginLeft: `${
                            publishedShift.shift_start *
                              4 *
                              widthOfEachQuarterTick -
                            start_qhnum * 4 * widthOfEachQuarterTick
                          }%`,
                          width: `${
                            (publishedShift.shift_length || (publishedShift.shift_end - publishedShift.shift_start)) *
                            4 *
                            widthOfEachQuarterTick
                          }%`,
                        }}
                      ></div>
                    ))}
                    {/* Published Meal Break (gray on published shift) */}
                    {showPublishedForShift[index2] && getCoveredPublishedShifts(shift).map((publishedShift) => (
                      publishedShift?.break_length > 0 && (
                      <div
                        key={`published-meal-break-${publishedShift.id || publishedShift.shift_start}`}
                        className="bg-slate-300 h-7 absolute z-5"
                        style={{
                          marginLeft: `${
                            publishedShift.break_start * 4 * widthOfEachQuarterTick -
                            start_qhnum * 4 * widthOfEachQuarterTick
                          }%`,
                          width: `${
                            publishedShift.break_length * 4 * widthOfEachQuarterTick
                          }%`,
                        }}
                      ></div>
                      )
                    ))}
                    {showPublishedForShift[index2] && getCoveredPublishedShifts(shift).map((publishedShift) => (
                      (publishedShift?.break_length_2 || 0) > 0 && (
                      <div
                        key={`published-meal-break-2-${publishedShift.id || publishedShift.shift_start}`}
                        className="bg-slate-300 h-7 absolute z-5"
                        style={{
                          marginLeft: `${
                            publishedShift.break_start_2 * 4 * widthOfEachQuarterTick -
                            start_qhnum * 4 * widthOfEachQuarterTick
                          }%`,
                          width: `${
                            publishedShift.break_length_2 * 4 * widthOfEachQuarterTick
                          }%`,
                        }}
                      ></div>
                      )
                    ))}
                    {/* Published Shift Outline (border only, on top) */}
                    {showPublishedForShift[index2] && getCoveredPublishedShifts(shift).map((publishedShift) => (
                      <div
                        key={`published-outline-${publishedShift.id || publishedShift.shift_start}`}
                        className="border border-slate-900 h-7 absolute z-10 bg-transparent"
                        style={{
                          marginLeft: `${
                            publishedShift.shift_start *
                              4 *
                              widthOfEachQuarterTick -
                            start_qhnum * 4 * widthOfEachQuarterTick
                          }%`,
                          width: `${
                            (publishedShift.shift_length || (publishedShift.shift_end - publishedShift.shift_start)) *
                            4 *
                            widthOfEachQuarterTick
                          }%`,
                        }}
                      ></div>
                    ))}
                    {/* Published Paid Breaks (emerald on published shift) */}
                    {showPublishedForShift[index2] && getCoveredPublishedShifts(shift).flatMap((publishedShift) => (
                      publishedShift?.paid_breaks?.map((paidBreak, paidBreakIndex) => {
                      const paidBreakDuration =
                        paidBreak?.break_duration || paidBreak?.break_length || 0;

                      if (
                        !paidBreak ||
                        paidBreak?.break_start === undefined ||
                        paidBreakDuration <= 0
                      ) {
                        return null;
                      }

                      return (
                        <div
                          key={`published-paid-break-${publishedShift.id || publishedShift.shift_start}-${paidBreakIndex}`}
                          className="bg-emerald-100 h-7 absolute"
                          style={{
                            marginLeft: `${
                              paidBreak.break_start * 4 * widthOfEachQuarterTick -
                              start_qhnum * 4 * widthOfEachQuarterTick
                            }%`,
                            width: `${
                              paidBreakDuration * 4 * widthOfEachQuarterTick
                            }%`,
                          }}
                        ></div>
                      );
                    }) || []
                    ))}
                    {/* Actual Shift Bar (purple) */}
                    <div
                      className="bg-violet-700 opacity-50 h-3 rounded-sm absolute"
                      style={{
                        marginLeft: `${
                          shift.shift_start * 4 * widthOfEachQuarterTick -
                          start_qhnum * 4 * widthOfEachQuarterTick
                        }%`,
                        width: `${
                          renderedShiftLength * 4 * widthOfEachQuarterTick
                        }%`,
                      }}
                    ></div>
                    {/* Actual Meal Breaks (solid purple on actual shift) */}
                    {getUnpaidBreaks(shift).map((breakEntry, breakIndex) => {
                      if (
                        breakEntry?.break_start === undefined ||
                        breakEntry?.break_length <= 0
                      ) {
                        return null;
                      }

                      // For in-progress breaks, extend to current time instead of using capped duration
                      const effectiveBreakLength = breakEntry.is_in_progress && currentBusinessQhNum != null
                        ? Math.max(currentBusinessQhNum - breakEntry.break_start, breakEntry.break_length)
                        : breakEntry.break_length;

                      // Clamp break so it doesn't extend past the shift bar end
                      const shiftEnd = renderedShiftEnd ?? (shift.shift_start + renderedShiftLength);
                      const breakEnd = breakEntry.break_start + effectiveBreakLength;
                      const clampedBreakLength = breakEnd > shiftEnd
                        ? Math.max(0, shiftEnd - breakEntry.break_start)
                        : effectiveBreakLength;
                      if (clampedBreakLength <= 0) {
                        return null;
                      }

                      return (
                        <div
                          key={`used-break-${index}-${index2}-${breakIndex}`}
                          className="bg-violet-900 opacity-100 h-3 rounded-sm absolute"
                          style={{
                            marginLeft: `${
                              breakEntry.break_start * 4 * widthOfEachQuarterTick -
                              start_qhnum * 4 * widthOfEachQuarterTick
                            }%`,
                            width: `${
                              clampedBreakLength * 4 * widthOfEachQuarterTick
                            }%`,
                          }}
                        ></div>
                      );
                    })}
                    {/* Actual Paid Breaks (fuchsia on actual shift) */}
                    {shift.paid_breaks?.map((paidBreak, paidBreakIndex) => {
                      const paidBreakDuration =
                        paidBreak?.break_duration || paidBreak?.break_length || 0;

                      if (
                        !paidBreak ||
                        paidBreak?.break_start === undefined ||
                        paidBreakDuration <= 0
                      ) {
                        return null;
                      }

                      // Clamp break so it doesn't extend past the shift bar end
                      const shiftEnd = renderedShiftEnd ?? (shift.shift_start + renderedShiftLength);
                      const paidBreakEnd = paidBreak.break_start + paidBreakDuration;
                      const clampedPaidBreakDuration = paidBreakEnd > shiftEnd
                        ? Math.max(0, shiftEnd - paidBreak.break_start)
                        : paidBreakDuration;
                      if (clampedPaidBreakDuration <= 0) {
                        return null;
                      }

                      return (
                        <div
                          key={`used-paid-break-${index}-${paidBreakIndex}`}
                          className="bg-fuchsia-400 h-3 rounded-sm absolute"
                          style={{
                            marginLeft: `${
                              paidBreak.break_start * 4 * widthOfEachQuarterTick -
                              start_qhnum * 4 * widthOfEachQuarterTick
                            }%`,
                            width: `${
                              clampedPaidBreakDuration * 4 * widthOfEachQuarterTick
                            }%`,
                          }}
                        ></div>
                      );
                    })}
                  </div>
                </div>
              );})}
            </div>
          ); })}
        </div>
        {transformed_used_schedule_data3.length > 0 && (
        <div className="w-full flex">
          <div className="w-[38%] py-2 border-r border-slate-300 flex justify-center items-center my-2">
            <div className="flex gap-x-4">
              <p className="text-slate-900 text-xxs font-bold">
                Employee Type:
              </p>
              <p className="text-slate-900 text-xxs">
                <span className="font-bold">[m]</span> Minor
              </p>
            </div>
          </div>
          <div className="w-[62%] flex justify-center items-center">
            <div className="flex items-center gap-x-4 flex-wrap justify-center">
              <p className="text-slate-900 text-xxs font-bold">Actual:</p>
              <div className="flex items-center gap-x-1">
                <div className="w-5 h-5 bg-violet-700 opacity-50"></div>
                <p className="text-slate-900 text-xxs">Shift</p>
              </div>
              <div className="flex items-center gap-x-1">
                <div className="w-5 h-5 bg-violet-900"></div>
                <p className="text-slate-900 text-xxs">Meal Break</p>
              </div>
              <div className="flex items-center gap-x-1">
                <div className="w-5 h-5 bg-fuchsia-400"></div>
                <p className="text-slate-900 text-xxs">Paid Break</p>
              </div>
              <p className="text-slate-900 text-xxs font-bold ml-2">Published:</p>
              <div className="flex items-center gap-x-1">
                <div className="w-5 h-5 border border-slate-900"></div>
                <p className="text-slate-900 text-xxs">Shift</p>
              </div>
              <div className="flex items-center gap-x-1">
                <div className="w-5 h-5 bg-slate-300"></div>
                <p className="text-slate-900 text-xxs">Meal Break</p>
              </div>
              <div className="flex items-center gap-x-1">
                <div className="w-5 h-5 bg-emerald-100"></div>
                <p className="text-slate-900 text-xxs">Paid Break</p>
              </div>
              {show_non_coverage_used && (
                <div className="flex items-center gap-x-1">
                  <div className="w-5 h-5 border border-slate-900 relative bg-diagonal-stripes"></div>
                  <p className="text-slate-900 text-xxs">Non-coverage</p>
                </div>
              )}
            </div>
          </div>
        </div>
        )}
      </>
    );
  };

  return (
            <div className="p-6" ref={componentRef}>
              <div>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center gap-x-2">
                    <h2 className="font-bold text-xl">
                      Live Action Report
                    </h2>
                    {props.store_name && (
                      <span className="text-slate-900 text-xl font-bold">
                        — {props.store_name}
                      </span>
                    )}
                    <div
                      className="w-2.5 h-2.5 bg-rose-600 rounded-full"
                      style={{ animation: 'blink 2s infinite' }}
                    />
                    <style>
                      {`
                        @keyframes blink {
                          0% { opacity: 1; }
                          50% { opacity: 0; }
                          100% { opacity: 1; }
                        }
                      `}
                    </style>
                  </div>
                  {!props.hidePrint && (
                    <div className="flex items-center gap-x-2">
                      <div className="text-slate-500 cursor-pointer">
                        <ReactToPrint
                          trigger={() => (
                            <IconButton>
                              <PrintIcon />
                            </IconButton>
                          )}
                          content={() => componentRef.current}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <h4 className="mt-1">
                  <span>{formatDateReadableWithDayOfWeek(props.date_str)}</span>
                </h4>
              </div>
              {/* Tab bar */}
              {props.activeTab && (
                <SubTabs
                  className="mt-3"
                  tabs={[
                    { key: "labor", label: "Labor", icon: BarChartIcon },
                    { key: "tasks", label: "Tasks", icon: AssignmentIcon },
                  ]}
                  activeTab={props.activeTab}
                  onChange={props.setActiveTab}
                />
              )}
              {/* Tasks tab content */}
              {props.activeTab === "tasks" ? (
                props.tasksPanel
              ) : (
              <>
              {/* Live Activity Feed */}
              {props.showActivityFeed && activityEvents.length > 0 && (
                <div className="border border-slate-900 mt-6">
                  <div className="w-full border-b border-slate-900 flex items-center px-4 py-3">
                    <h3 className="text-sm font-bold text-slate-900">Live Activity</h3>
                    <div
                      className="w-2 h-2 bg-rose-600 rounded-full ml-2"
                      style={{ animation: 'blink 2s infinite' }}
                    />
                    {dismissedAlerts.size > 0 && (
                      <button
                        className="ml-auto text-xs text-slate-500 hover:text-slate-700"
                        onClick={() => setDismissedAlerts(new Set())}
                      >
                        Show dismissed ({dismissedAlerts.size})
                      </button>
                    )}
                  </div>
                  <div className="divide-y divide-slate-200">
                    {activityEvents.filter((e) => !dismissedAlerts.has(e.key)).map((event) => (
                      <div key={event.key} className="flex items-center px-4 py-2.5">
                        {event.isLaborLaw ? (
                          <WarningAmberIcon
                            sx={{ fontSize: 14, flexShrink: 0, mr: 1 }}
                            className={event.severity === "alert" ? "text-rose-500" : "text-amber-500"}
                          />
                        ) : (
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 mr-3 ${
                            event.severity === "alert" ? "bg-rose-500" :
                            event.severity === "warning" ? "bg-amber-500" : "bg-emerald-500"
                          }`} />
                        )}
                        <div className="flex-1 min-w-0">
                          {event.isSalesAlert ? (
                            <>
                              <span className="text-xs font-semibold text-slate-900">{event.name}</span>
                              <span className="text-xs text-slate-500 ml-1.5">— {event.message}</span>
                            </>
                          ) : (
                            <>
                              <span className="text-xs font-semibold text-slate-900">{formatName(event.name)}</span>
                              <span className="text-xs text-slate-500 ml-1.5">— {event.message}</span>
                            </>
                          )}
                        </div>
                        {/* Classify before Notify so Notify stays in the same
                            column whether or not a row is classifiable. */}
                        {event.attendanceClassifiable && (
                          <button
                            className={`ml-3 px-4 py-2 text-xs font-medium rounded flex-shrink-0 ${
                              classifyingAlerts.has(event.key)
                                ? "text-amber-400 bg-amber-50"
                                : "text-amber-800 bg-amber-100 hover:bg-amber-200"
                            }`}
                            disabled={classifyingAlerts.has(event.key)}
                            onClick={(e) => {
                              setClassificationEvent(event);
                              setClassificationMenuAnchor(e.currentTarget);
                            }}
                          >
                            {classifyingAlerts.has(event.key) ? "Classifying..." : "Classify"}
                          </button>
                        )}
                        {event.notifyMessage && (
                          <button
                            className={`ml-3 px-4 py-2 text-xs font-medium rounded flex-shrink-0 ${
                              notifiedAlerts.has(event.key)
                                ? "text-emerald-700 bg-emerald-100"
                                : sendingAlerts.has(event.key)
                                  ? "text-violet-400 bg-violet-50"
                                  : "text-violet-700 bg-violet-100 hover:bg-violet-200"
                            }`}
                            disabled={notifiedAlerts.has(event.key) || sendingAlerts.has(event.key)}
                            onClick={async () => {
                              setSendingAlerts((prev) => new Set([...prev, event.key]));
                              try {
                                await axios.post(
                                  API_send_notification_to_employee,
                                  {
                                    store_id: props.store_id,
                                    store_team_member_id: event.stm_id,
                                    title: "Shift Reminder",
                                    body: event.notifyMessage,
                                  },
                                  getDefaultAxiosConfig()
                                );
                                setNotifiedAlerts((prev) => new Set([...prev, event.key]));
                              } catch (error) {
                                console.error("Failed to send notification:", error);
                              } finally {
                                setSendingAlerts((prev) => {
                                  const next = new Set(prev);
                                  next.delete(event.key);
                                  return next;
                                });
                              }
                            }}
                          >
                            {notifiedAlerts.has(event.key) ? "Sent" : sendingAlerts.has(event.key) ? "Sending..." : "Notify"}
                          </button>
                        )}
                        <button
                          className="ml-3 px-4 py-2 text-xs font-medium text-slate-500 bg-slate-100 hover:bg-slate-200 rounded flex-shrink-0"
                          onClick={() => setDismissedAlerts((prev) => new Set([...prev, event.key]))}
                        >
                          Dismiss
                        </button>
                      </div>
                    ))}
                  </div>
                  <Menu
                    anchorEl={classificationMenuAnchor}
                    open={Boolean(classificationMenuAnchor)}
                    onClose={closeClassificationMenu}
                  >
                    {MISSED_SHIFT_REASON_OPTIONS.map((option) => (
                      <MenuItem
                        key={option.value}
                        onClick={() => handleClassifyAttendance(option.value)}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </div>
              )}

              {/* Wrapper for both tables with single red line spanning all */}
              <div className="mt-6 relative">
                {/* Last Updated label and vertical line - spans both tables */}
                {isDataLineVisible && lastUpdatedTimeString && (
                  <div className="absolute top-0 bottom-0 w-full flex pointer-events-none" style={{ zIndex: 40, paddingLeft: '1px', paddingRight: '1px' }}>
                    {/* Match the 38%/62% split inside the bordered containers */}
                    <div className="w-[38%]" />
                    <div className="w-[62%] relative">
                      <div
                        className="absolute -top-5 text-xxs font-semibold text-rose-600 whitespace-nowrap"
                        style={{
                          left: `calc(${clampedDataLinePositionInChart}% - 1px)`,
                          transform: dataLineLabelTransform,
                        }}
                      >
                        Last Updated: {lastUpdatedTimeString}
                      </div>
                      <div
                        className="absolute top-0 bottom-0 w-0.5 bg-rose-600"
                        style={{ left: `calc(${clampedDataLinePositionInChart}% - 1px)` }}
                      />
                    </div>
                  </div>
                )}

                {renderUsedScheduleTable()}

                <div className="border border-slate-900 mt-4">
                <div className="w-full border-b border-slate-900 flex">
                  <div className="w-[38%] flex py-4 border-r border-slate-900">
                    <div className="w-full flex items-center px-4">
                      <p className="text-slate-900 text-xxs">
                        <span className="font-bold">Metrics </span>
                      </p>
                    </div>
                  </div>
                  <div className="w-[62%] flex">
                    {Array.from({ length: numOfHourBoxes }).map((_, index) => (
                      <div
                        key={index}
                        className={`${
                          index < numOfHourBoxes - 1 ? "border-r" : ""
                        } border-slate-900 flex flex-col`}
                        style={{ width: `${widthOfEachHourBox}%` }}
                      >
                        <div className="h-[70%] flex items-center justify-center">
                          <p className="text-slate-900 text-xs">
                            {get_t_str_shortened_max(index + start_qhnum)}
                          </p>
                        </div>
                        <div className="h-[30%] flex">
                          <div className="w-[50%] border-r border-slate-900"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {filteredMetrics.hasOwnProperty("Sales") && (
                  <div className={`w-full flex`}>
                    <div
                      className={`w-[38%] flex py-2 border-r border-slate-900`}
                    >
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[125px]">
                          Total Sales
                        </p>
                        <p className="text-slate-900 text-xxs">
                          {totalSales?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </p>
                        <p className="text-slate-900 text-xxs w-[125px] flex justify-end">
                          Sales
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex">
                      {filteredMetrics?.["Sales"]?.map((value, hourIndex) => {
                        const isFuturePeriod = value.qh_num + 0.5 > dataQhNum;
                        return (
                          <div
                            key={hourIndex}
                            className={`${
                              hourIndex < numOfHourBoxes * 2 - 1
                                ? "border-r border-dashed"
                                : ""
                            } border-slate-200 flex flex-col`}
                            style={{ width: `${widthOfEachHalfTick}%` }}
                          >
                            <div className="h-[100%] flex items-center justify-center">
                              <p className="text-slate-900 text-xxs">
                                {isFuturePeriod
                                  ? "-"
                                  : value.total_value < 1000
                                    ? Math.round(value.total_value)
                                    : convertToK(value.total_value)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {filteredForecastMetrics.hasOwnProperty("Sales") && (
                  <div className="w-full flex bg-slate-50">
                    <div className="w-[38%] flex py-2 border-r border-slate-900">
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-500 text-xxs w-[125px]">
                          Forecasted Sales
                        </p>
                        <p className="text-slate-500 text-xxs">
                          {totalForecastedSalesUpToNow?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                          {" / "}
                          {totalForecastedSales?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </p>
                        <p className="text-slate-500 text-xxs w-[125px] flex justify-end">
                          Forecast
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex">
                      {filteredForecastMetrics?.["Sales"]?.map((value, hourIndex) => {
                        return (
                          <div
                            key={hourIndex}
                            className={`${
                              hourIndex < numOfHourBoxes * 2 - 1
                                ? "border-r border-dashed"
                                : ""
                            } border-slate-200 flex flex-col`}
                            style={{ width: `${widthOfEachHalfTick}%` }}
                          >
                            <div className="h-[100%] flex items-center justify-center">
                              <p className="text-slate-500 text-xxs">
                                {value.total_value < 1000
                                  ? Math.round(value.total_value)
                                  : convertToK(value.total_value)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {filteredMetrics.hasOwnProperty("Sales") && filteredForecastMetrics.hasOwnProperty("Sales") && (
                  <div className="w-full flex">
                    <div className="w-[38%] flex py-2 border-r border-slate-900">
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[125px]">
                          Sales Variance
                        </p>
                        <p className={`text-xxs ${
                          calculateVariancePercent(totalSales, totalForecastedSalesUpToNow) >= 0
                            ? "text-emerald-700"
                            : "text-rose-700"
                        }`}>
                          {calculateVariancePercent(totalSales, totalForecastedSalesUpToNow) >= 0 ? "+" : ""}
                          {calculateVariancePercent(totalSales, totalForecastedSalesUpToNow).toFixed(1)}%
                        </p>
                        <p className="text-slate-900 text-xxs w-[125px] flex justify-end">
                          Variance
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex">
                      {filteredMetrics?.["Sales"]?.map((value, hourIndex) => {
                        const isFuturePeriod = value.qh_num + 0.5 > dataQhNum;
                        const forecastValue = filteredForecastMetrics?.["Sales"]?.[hourIndex]?.total_value || 0;
                        const variance = calculateVariancePercent(value.total_value, forecastValue);
                        return (
                          <div
                            key={hourIndex}
                            className={`${
                              hourIndex < numOfHourBoxes * 2 - 1
                                ? "border-r border-dashed"
                                : ""
                            } border-slate-200 flex flex-col`}
                            style={{ width: `${widthOfEachHalfTick}%` }}
                          >
                            <div className="h-[100%] flex items-center justify-center">
                              <p className={`text-[9px] ${
                                isFuturePeriod ? "text-slate-400" : variance >= 0 ? "text-emerald-700" : "text-rose-700"
                              }`}>
                                {isFuturePeriod ? "-" : forecastValue > 0 ? `${variance.toFixed(0)}%` : "-"}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {filteredMetrics.hasOwnProperty("Transactions") && (
                  <div className={`w-full flex`}>
                    <div
                      className={`w-[38%] flex py-2 border-r border-slate-900`}
                    >
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[125px]">
                          Total Transactions
                        </p>
                        <p className="text-slate-900 text-xxs">
                          {Math.round(totalTransactions)}
                        </p>
                        <p className="text-slate-900 text-xxs w-[125px] flex justify-end">
                          Transactions
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex">
                      {filteredMetrics?.["Transactions"]?.map(
                        (value, hourIndex) => {
                          const isFuturePeriod = value.qh_num + 0.5 > dataQhNum;
                          return (
                            <div
                              key={hourIndex}
                              className={`${
                                hourIndex < numOfHourBoxes * 2 - 1
                                  ? "border-r border-dashed"
                                  : ""
                              } border-slate-200 flex flex-col`}
                              style={{ width: `${widthOfEachHalfTick}%` }}
                            >
                              <div className="h-[100%] flex items-center justify-center">
                                <p className="text-slate-900 text-xxs">
                                  {isFuturePeriod ? "-" : Math.round(value.total_value)}
                                </p>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}
                {filteredForecastMetrics.hasOwnProperty("Transactions") && (
                  <div className="w-full flex bg-slate-50">
                    <div className="w-[38%] flex py-2 border-r border-slate-900">
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-500 text-xxs w-[125px]">
                          Forecasted Transactions
                        </p>
                        <p className="text-slate-500 text-xxs">
                          {Math.round(totalForecastedTransactionsUpToNow)} / {Math.round(totalForecastedTransactions)}
                        </p>
                        <p className="text-slate-500 text-xxs w-[125px] flex justify-end">
                          Forecast
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex">
                      {filteredForecastMetrics?.["Transactions"]?.map((value, hourIndex) => {
                        return (
                          <div
                            key={hourIndex}
                            className={`${
                              hourIndex < numOfHourBoxes * 2 - 1
                                ? "border-r border-dashed"
                                : ""
                            } border-slate-200 flex flex-col`}
                            style={{ width: `${widthOfEachHalfTick}%` }}
                          >
                            <div className="h-[100%] flex items-center justify-center">
                              <p className="text-slate-500 text-xxs">
                                {Math.round(value.total_value)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {filteredMetrics.hasOwnProperty("Transactions") && filteredForecastMetrics.hasOwnProperty("Transactions") && (
                  <div className="w-full flex">
                    <div className="w-[38%] flex py-2 border-r border-slate-900">
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[125px]">
                          Transactions Variance
                        </p>
                        <p className={`text-xxs ${
                          calculateVariancePercent(totalTransactions, totalForecastedTransactionsUpToNow) >= 0
                            ? "text-emerald-700"
                            : "text-rose-700"
                        }`}>
                          {calculateVariancePercent(totalTransactions, totalForecastedTransactionsUpToNow) >= 0 ? "+" : ""}
                          {calculateVariancePercent(totalTransactions, totalForecastedTransactionsUpToNow).toFixed(1)}%
                        </p>
                        <p className="text-slate-900 text-xxs w-[125px] flex justify-end">
                          Variance
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex">
                      {filteredMetrics?.["Transactions"]?.map((value, hourIndex) => {
                        const isFuturePeriod = value.qh_num + 0.5 > dataQhNum;
                        const forecastValue = filteredForecastMetrics?.["Transactions"]?.[hourIndex]?.total_value || 0;
                        const variance = calculateVariancePercent(value.total_value, forecastValue);
                        return (
                          <div
                            key={hourIndex}
                            className={`${
                              hourIndex < numOfHourBoxes * 2 - 1
                                ? "border-r border-dashed"
                                : ""
                            } border-slate-200 flex flex-col`}
                            style={{ width: `${widthOfEachHalfTick}%` }}
                          >
                            <div className="h-[100%] flex items-center justify-center">
                              <p className={`text-[9px] ${
                                isFuturePeriod ? "text-slate-400" : variance >= 0 ? "text-emerald-700" : "text-rose-700"
                              }`}>
                                {isFuturePeriod ? "-" : forecastValue > 0 ? `${variance.toFixed(0)}%` : "-"}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {filteredMetrics.hasOwnProperty("Items") && (
                  <div className={`w-full flex`}>
                    <div
                      className={`w-[38%] flex py-2 border-r border-slate-900`}
                    >
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[125px]">
                          Total Items
                        </p>
                        <p className="text-slate-900 text-xxs">
                          {Math.round(totalItems)}
                        </p>
                        <p className="text-slate-900 text-xxs w-[125px] flex justify-end">
                          Items
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex">
                      {filteredMetrics?.["Items"]?.map((value, hourIndex) => {
                        const isFuturePeriod = value.qh_num + 0.5 > dataQhNum;
                        return (
                          <div
                            key={hourIndex}
                            className={`${
                              hourIndex < numOfHourBoxes * 2 - 1
                                ? "border-r border-dashed"
                                : ""
                            } border-slate-200 flex flex-col`}
                            style={{ width: `${widthOfEachHalfTick}%` }}
                          >
                            <div className="h-[100%] flex items-center justify-center">
                              <p className="text-slate-900 text-xxs">
                                {isFuturePeriod ? "-" : Math.round(value.total_value)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {filteredForecastMetrics.hasOwnProperty("Items") && (
                  <div className="w-full flex bg-slate-50">
                    <div className="w-[38%] flex py-2 border-r border-slate-900">
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-500 text-xxs w-[125px]">
                          Forecasted Items
                        </p>
                        <p className="text-slate-500 text-xxs">
                          {Math.round(totalForecastedItemsUpToNow)} / {Math.round(totalForecastedItems)}
                        </p>
                        <p className="text-slate-500 text-xxs w-[125px] flex justify-end">
                          Forecast
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex">
                      {filteredForecastMetrics?.["Items"]?.map((value, hourIndex) => {
                        return (
                          <div
                            key={hourIndex}
                            className={`${
                              hourIndex < numOfHourBoxes * 2 - 1
                                ? "border-r border-dashed"
                                : ""
                            } border-slate-200 flex flex-col`}
                            style={{ width: `${widthOfEachHalfTick}%` }}
                          >
                            <div className="h-[100%] flex items-center justify-center">
                              <p className="text-slate-500 text-xxs">
                                {Math.round(value.total_value)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {filteredMetrics.hasOwnProperty("Items") && filteredForecastMetrics.hasOwnProperty("Items") && (
                  <div className="w-full flex">
                    <div className="w-[38%] flex py-2 border-r border-slate-900">
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[125px]">
                          Items Variance
                        </p>
                        <p className={`text-xxs ${
                          calculateVariancePercent(totalItems, totalForecastedItemsUpToNow) >= 0
                            ? "text-emerald-700"
                            : "text-rose-700"
                        }`}>
                          {calculateVariancePercent(totalItems, totalForecastedItemsUpToNow) >= 0 ? "+" : ""}
                          {calculateVariancePercent(totalItems, totalForecastedItemsUpToNow).toFixed(1)}%
                        </p>
                        <p className="text-slate-900 text-xxs w-[125px] flex justify-end">
                          Variance
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex">
                      {filteredMetrics?.["Items"]?.map((value, hourIndex) => {
                        const isFuturePeriod = value.qh_num + 0.5 > dataQhNum;
                        const forecastValue = filteredForecastMetrics?.["Items"]?.[hourIndex]?.total_value || 0;
                        const variance = calculateVariancePercent(value.total_value, forecastValue);
                        return (
                          <div
                            key={hourIndex}
                            className={`${
                              hourIndex < numOfHourBoxes * 2 - 1
                                ? "border-r border-dashed"
                                : ""
                            } border-slate-200 flex flex-col`}
                            style={{ width: `${widthOfEachHalfTick}%` }}
                          >
                            <div className="h-[100%] flex items-center justify-center">
                              <p className={`text-[9px] ${
                                isFuturePeriod ? "text-slate-400" : variance >= 0 ? "text-emerald-700" : "text-rose-700"
                              }`}>
                                {isFuturePeriod ? "-" : forecastValue > 0 ? `${variance.toFixed(0)}%` : "-"}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className={`w-full h-5 flex`}>
                  <div
                    className={`w-[38%] flex py-2 border-r border-slate-900`}
                  ></div>
                  <div className="w-[62%] flex">
                    {filteredMetrics?.["Sales"]?.map((value, hourIndex) => {
                      return (
                        <div
                          key={hourIndex}
                          className={`${
                            hourIndex < numOfHourBoxes * 2 - 1
                              ? "border-r border-dashed"
                              : ""
                          } border-slate-200 flex flex-col`}
                          style={{ width: `${widthOfEachHalfTick}%` }}
                        >
                          <div className="h-[100%] flex items-center justify-center"></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {filteredMetrics.hasOwnProperty(primaryHoursKey) && (
                  <div className={`w-full flex`}>
                    <div
                      className={`w-[38%] flex py-2 border-r border-slate-900`}
                    >
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[125px]">
                          {primaryHoursKey}
                        </p>
                        <p className="text-slate-900 text-xxs">
                          {Math.round(totalPrimaryHours)}
                        </p>
                        <p className="text-slate-900 text-xxs w-[125px] flex justify-end">
                          {primaryLaborKey}
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex">
                      {filteredMetrics?.[primaryLaborDataKey]?.map(
                        (value, hourIndex) => {
                          const isFuturePeriod = value.qh_num + 0.5 > dataQhNum;
                          return (
                            <div
                              key={hourIndex}
                              className={`${
                                hourIndex < numOfHourBoxes * 2 - 1
                                  ? "border-r border-dashed"
                                  : ""
                              } border-slate-200 flex flex-col`}
                              style={{ width: `${widthOfEachHalfTick}%` }}
                            >
                              <div className="h-[100%] flex items-center justify-center">
                                <p className="text-slate-900 text-xxs">
                                  {isFuturePeriod ? "-" : Math.round(value.total_value)}
                                </p>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}
                {filteredMetrics.hasOwnProperty(secondaryHoursKey) && (
                  <div className={`w-full flex`}>
                    <div
                      className={`w-[38%] flex py-2 border-r border-slate-900`}
                    >
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[125px]">
                          {secondaryHoursKey}
                        </p>
                        <p className="text-slate-900 text-xxs">
                          {Math.round(totalSecondaryHours)}
                        </p>
                        <p className="text-slate-900 text-xxs w-[125px] flex justify-end">
                          {secondaryLaborKey}
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex">
                      {filteredMetrics?.[secondaryLaborKey]?.map(
                        (value, hourIndex) => {
                          const isFuturePeriod = value.qh_num + 0.5 > dataQhNum;
                          return (
                            <div
                              key={hourIndex}
                              className={`${
                                hourIndex < numOfHourBoxes * 2 - 1
                                  ? "border-r border-dashed"
                                  : ""
                              } border-slate-200 flex flex-col`}
                              style={{ width: `${widthOfEachHalfTick}%` }}
                            >
                              <div className="h-[100%] flex items-center justify-center">
                                <p className="text-slate-900 text-xxs">
                                  {isFuturePeriod ? "-" : Math.round(value.total_value)}
                                </p>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}
                {filteredMetrics.hasOwnProperty(trainingHoursKey) && (
                  <div className={`w-full flex`}>
                    <div
                      className={`w-[38%] flex py-2 border-r border-slate-900`}
                    >
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[125px]">
                          {trainingHoursKey}
                        </p>
                        <p className="text-slate-900 text-xxs">
                          {Math.round(totalTrainingHours * 100) / 100}
                        </p>
                        <p className="text-slate-900 text-xxs w-[125px] flex justify-end">
                          Training Labor
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex">
                      {filteredMetrics?.[trainingHoursKey]?.map(
                        (value, hourIndex) => {
                          const isFuturePeriod = value.qh_num + 0.5 > dataQhNum;
                          return (
                            <div
                              key={hourIndex}
                              className={`${
                                hourIndex < numOfHourBoxes * 2 - 1
                                  ? "border-r border-dashed"
                                  : ""
                              } border-slate-200 flex flex-col`}
                              style={{ width: `${widthOfEachHalfTick}%` }}
                            >
                              <div className="h-[100%] flex items-center justify-center">
                                <p className="text-slate-900 text-xxs">
                                  {isFuturePeriod ? "-" : Math.round(value.total_value*2 * 100) / 100}
                                </p>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}
                {filteredMetrics.hasOwnProperty(adminHoursKey) && (
                  <div className={`w-full flex`}>
                    <div
                      className={`w-[38%] flex py-2 border-r border-slate-900`}
                    >
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[125px]">
                          {adminHoursKey}
                        </p>
                        <p className="text-slate-900 text-xxs">
                          {Math.round(totalAdminHours * 100) / 100}
                        </p>
                        <p className="text-slate-900 text-xxs w-[125px] flex justify-end">
                          Admin Labor
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex">
                      {filteredMetrics?.[adminHoursKey]?.map((value, hourIndex) => {
                        const isFuturePeriod = value.qh_num + 0.5 > dataQhNum;
                        return (
                          <div
                            key={hourIndex}
                            className={`${
                              hourIndex < numOfHourBoxes * 2 - 1
                                ? "border-r border-dashed"
                                : ""
                            } border-slate-200 flex flex-col`}
                            style={{ width: `${widthOfEachHalfTick}%` }}
                          >
                            <div className="h-[100%] flex items-center justify-center">
                              <p className="text-slate-900 text-xxs">
                                {isFuturePeriod ? "-" : Math.round(value.total_value*2 * 100) / 100}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className={`w-full h-5 flex`}>
                  <div
                    className={`w-[38%] flex py-2 border-r border-slate-900`}
                  ></div>
                  <div className="w-[62%] flex">
                    {filteredMetrics?.["Sales"]?.map((value, hourIndex) => {
                      return (
                        <div
                          key={hourIndex}
                          className={`${
                            hourIndex < numOfHourBoxes * 2 - 1
                              ? "border-r border-dashed"
                              : ""
                          } border-slate-200 flex flex-col`}
                          style={{ width: `${widthOfEachHalfTick}%` }}
                        >
                          <div className="h-[100%] flex items-center justify-center"></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {filteredMetrics.hasOwnProperty("Sales") && (
                  <div className={`w-full flex`}>
                    <div
                      className={`w-[38%] flex py-2 border-r border-slate-900`}
                    >
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[125px]">
                          Total Actual SPLH
                        </p>
                        <p className="text-slate-900 text-xxs">
                          $
                          {totalSecondaryHours === 0
                            ? 0
                            : Math.round(
                                Math.round(totalSales) /
                                  Math.round(totalSecondaryHours)
                              )}
                        </p>
                        <p className="text-slate-900 text-xxs w-[125px] flex justify-end">
                          Actual SPLH
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex">
                      {filteredMetrics?.["Sales"]?.map((value, hourIndex) => {
                        const isFuturePeriod = value.qh_num + 0.5 > dataQhNum;
                        return (
                          <div
                            key={hourIndex}
                            className={`${
                              hourIndex < numOfHourBoxes * 2 - 1
                                ? "border-r border-dashed"
                                : ""
                            } border-slate-200 flex flex-col`}
                            style={{ width: `${widthOfEachHalfTick}%` }}
                          >
                            <div className="h-[100%] flex items-center justify-center">
                              <p className="text-slate-900 text-xxs">
                                {isFuturePeriod
                                  ? "-"
                                  : filteredMetrics?.[secondaryHoursKey]?.[
                                      hourIndex
                                    ]?.total_value !== 0
                                    ? Math.round(
                                        value.total_value /
                                          filteredMetrics?.[secondaryHoursKey]?.[
                                            hourIndex
                                          ]?.total_value
                                      ) < 1000
                                      ? Math.round(
                                          value.total_value /
                                            filteredMetrics?.[
                                              secondaryHoursKey
                                            ]?.[hourIndex]?.total_value
                                        )
                                      : convertToK(
                                          Math.round(
                                            value.total_value /
                                              filteredMetrics?.[
                                                secondaryHoursKey
                                              ]?.[hourIndex]?.total_value
                                          )
                                        )
                                    : 0}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {filteredMetrics.hasOwnProperty("Transactions") && (
                  <div className={`w-full flex`}>
                    <div
                      className={`w-[38%] flex py-2 border-r border-slate-900`}
                    >
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[125px]">
                          Total Actual TPLH
                        </p>
                        <p className="text-slate-900 text-xxs">
                          {totalSecondaryHours === 0
                            ? 0
                            : Math.round(
                                Math.round(totalTransactions) /
                                  Math.round(totalSecondaryHours)
                              )}
                        </p>
                        <p className="text-slate-900 text-xxs w-[125px] flex justify-end">
                          Actual TPLH
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex">
                      {filteredMetrics?.["Transactions"]?.map(
                        (value, hourIndex) => {
                          const isFuturePeriod = value.qh_num + 0.5 > dataQhNum;
                          return (
                            <div
                              key={hourIndex}
                              className={`${
                                hourIndex < numOfHourBoxes * 2 - 1
                                  ? "border-r border-dashed"
                                  : ""
                              } border-slate-200 flex flex-col`}
                              style={{ width: `${widthOfEachHalfTick}%` }}
                            >
                              <div className="h-[100%] flex items-center justify-center">
                                <p className="text-slate-900 text-xxs">
                                  {isFuturePeriod
                                    ? "-"
                                    : filteredMetrics?.[secondaryHoursKey]?.[
                                        hourIndex
                                      ]?.total_value !== 0
                                      ? Math.round(
                                          value.total_value /
                                            filteredMetrics?.[
                                              secondaryHoursKey
                                            ]?.[hourIndex]?.total_value
                                        )
                                      : 0}
                                </p>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}
                {filteredMetrics.hasOwnProperty("Items") && (
                  <div className={`w-full flex`}>
                    <div
                      className={`w-[38%] flex py-2 border-r border-slate-900`}
                    >
                      <div className="w-full flex items-center justify-between px-4">
                        <p className="text-slate-900 text-xxs w-[125px]">
                          Total Actual IPLH
                        </p>
                        <p className="text-slate-900 text-xxs">
                          {totalSecondaryHours === 0
                            ? 0
                            : Math.round(
                                Math.round(totalItems) /
                                  Math.round(totalSecondaryHours)
                              )}
                        </p>
                        <p className="text-slate-900 text-xxs w-[125px] flex justify-end">
                          Actual IPLH
                        </p>
                      </div>
                    </div>
                    <div className="w-[62%] flex">
                      {filteredMetrics?.["Items"]?.map((value, hourIndex) => {
                        const isFuturePeriod = value.qh_num + 0.5 > dataQhNum;
                        return (
                          <div
                            key={hourIndex}
                            className={`${
                              hourIndex < numOfHourBoxes * 2 - 1
                                ? "border-r border-dashed"
                                : ""
                            } border-slate-200 flex flex-col`}
                            style={{ width: `${widthOfEachHalfTick}%` }}
                          >
                            <div className="h-[100%] flex items-center justify-center">
                              <p className="text-slate-900 text-xxs">
                                {isFuturePeriod
                                  ? "-"
                                  : filteredMetrics?.[secondaryHoursKey]?.[
                                      hourIndex
                                    ]?.total_value !== 0
                                    ? Math.round(
                                        value.total_value /
                                          filteredMetrics?.[secondaryHoursKey]?.[
                                            hourIndex
                                          ]?.total_value
                                      )
                                    : 0}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                </div>
              </div>
              </>
              )}

            </div>
  );
}
